import { Injectable } from '@angular/core';
import { FormGroup, ValidatorFn } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';

import { BehaviorSubject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { ValidateFormFields } from '@library/common/functions';

import { AppConfigService } from '@library/app.config';
import { Shipment, ShipmentTypes, ShipmentModes, ShipmentFactory } from '@library/common/models';
import { RelativeDateService } from '@library/common/datehandler';

import * as moment_ from 'moment';
const moment = moment_;

@Injectable({
   providedIn: 'root'
})
export class ShipmentService {

   template = false;
   new = false;

   highlightInvalid$: BehaviorSubject<boolean> = new BehaviorSubject( false );
   focused: BehaviorSubject<string> = new BehaviorSubject('');
   template$: BehaviorSubject<boolean> = new BehaviorSubject( false );
   configs: any = {};

   get shipment() {
      return this.shipmentForm?.getRawValue();
   }
   set shipment( shipment: Shipment<any, any> ) {
      shipment.pdatefrom = this.template$.value ? shipment.pdatefrom : shipment.pdatefrom ? this.relativeDates.getIsoDate( shipment.pdatefrom ) : '';
      shipment.pdateto = this.template$.value ? shipment.pdateto : shipment.pdateto ? this.relativeDates.getIsoDate( shipment.pdateto ) : '';
      shipment.ddatefrom = this.template$.value ? shipment.ddatefrom : shipment.ddatefrom ? this.relativeDates.getIsoDate( shipment.ddatefrom ) : '';
      shipment.ddateto = this.template$.value ? shipment.ddateto : shipment.ddateto ? this.relativeDates.getIsoDate( shipment.ddateto ) : '';
      this.shipmentForm = ShipmentFactory.formGroup( shipment );
   }

   shipmentForm$: BehaviorSubject<FormGroup> = new BehaviorSubject( null );

   shipmentUpdate$: BehaviorSubject<boolean> = new BehaviorSubject( null );

   private _shipmentForm: FormGroup;
   get shipmentForm() { return this._shipmentForm; }
   set shipmentForm( group: FormGroup ) {
      this.new = group ? false : true;
      this._shipmentForm = group || ShipmentFactory.formGroup();

      // set validators from app config
      this.appConfig.setValidators( this.shipmentForm, this.appConfig.config.booking );
      this.configs = this.appConfig.configObj.booking;

      // set up listener for temp
      this.temp = this.shipment;
      if ( this.tempListener$ ) { this.tempListener$.unsubscribe(); }
      this.tempListener$ = this._shipmentForm.valueChanges.subscribe( _ => this.temp = this.shipment );

      this.shipmentForm$.next( this._shipmentForm );
   }

   get disabled() { return this.shipmentForm && this.shipmentForm.disabled; }
   set disabled( value: boolean ) {
      // if ( value === this.shipmentForm.disabled ) { return; }
      value ? this.shipmentForm.disable() : this.shipmentForm.enable();
   }

   set validators( validators: ValidatorFn ) {
      if ( !this.shipment ) {
         console.log( 'set validators error:', 'shipment is not set' );

         return;
      }

      this.shipmentForm.setValidators( validators );
      this.shipmentForm.updateValueAndValidity();
   }

   tempListener$: Subscription;
   get temp() {
      if ( sessionStorage.getItem( 'TempShipment' )) {
         return JSON.parse( sessionStorage.getItem( 'TempShipment' ));
      }
   }
   set temp( shipment: Shipment<any, any> ) {
      const temp = { ...shipment };

      if ( temp.attachments ) { delete( temp.attachments ); }

      // console.log( 'TempShipment', shipment );
      sessionStorage.setItem( 'TempShipment', JSON.stringify( this.shrinkObj( temp )));
      localStorage.removeItem( 'temp-shipment' ); // removing old storage location
   }

   get shipType(): ShipmentTypes { return this.shipmentForm && this.shipmentForm.get( 'shipType' ).value; }
   get mode(): ShipmentModes { return this.shipmentForm && this.shipmentForm.get( 'mode' ).value; }

   constructor(
      public snackbar: MatSnackBar,
      public appConfig: AppConfigService,
      public relativeDates: RelativeDateService,
   ) {
      this.highlightInvalid$.pipe(
         filter( bool => bool ),
      ).subscribe( _ =>
         this.snackbar.open( 'Required fields have been highlighted', null, { duration: 5000 }));

      this.appConfig.configLoaded$.subscribe( _ =>
         setTimeout( () => this.shipmentForm = this.shipmentForm ));
   }

   validateFormFields( formGroup: FormGroup = this.shipmentForm ): void {
      ValidateFormFields( formGroup );
      this.highlightInvalid$.next( true );
   }

   reset() {
      this._shipmentForm = null;
      this.tempListener$?.unsubscribe();
      this.tempListener$ = null;
      sessionStorage.removeItem( 'TempShipment' );
      this.shipmentForm$.next( null );
      this.highlightInvalid$.next( false );
   }

   shrinkObj<T>( obj: T ): T | undefined {
      if ( !obj || !Object.keys( obj ).length ) { return undefined; }
      if ( obj instanceof Array && !obj.length ) { return undefined; }

      if ( !( obj instanceof Object )) { return obj; }

      const shrunkObj = Object.entries( obj )
         .filter(([_, v]) => !!v )
         .reduce((acc, [k, v]) => ({
            ...acc,
            [k]: v instanceof Array ? v.map( a => this.shrinkObj( a ))
               : v instanceof moment ? moment( v ).toISOString()
               : v instanceof Date ? new Date( v ).toISOString()
               : v instanceof Object ? this.shrinkObj( v )
               : v
         }), {} as T );

      if ( !Object.keys( shrunkObj ).length ) { return undefined; }

      return shrunkObj;
   }
}
